<template>
  <component
    :is="computedTag"
    ref="button"
    :type="type"
    :to="to"
    class="ak-action-grid__item"
    :class="actionClasses"
    :disabled="disabled"
    v-bind="$attrs"
    @click="clicked"
  >
    <ak-icon
      v-if="icon"
      :type="icon"
      :size="iconSize"
      class="ak-action-grid__item-icon"
      :class="{ 'mr-tiny': horizontal }"
    />
    <slot />
  </component>
</template>

<script>
import { computed, defineComponent } from 'vue'
import { nativeTypes, types, sizes } from './ActionGridItem.config'
import { sizes as iconSizes, icons as iconTypes } from '../icon/Icon.config'
import AkIcon from '../icon/Icon.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-action-grid theme="dark">...</ak-action-grid>
   */

  name: 'AkActionGridItem',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [AkIcon.name]: AkIcon,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @prop {Function} validator - Ensures a valid type is used
     * @prop {String} type - The type of the property
     * @prop {String} default - The default value of the property
     * @description Controls the type of the button component that will
     * be rendered i.e submit/reset/button
     */
    type: {
      validator: (v) => nativeTypes.indexOf(v) >= 0,
      type: String,
      default: undefined,
    },

    /**
     * @type {Object}
     * @prop {Function} validator - Ensures a valid type is used
     * @prop {String} type - The type of the property
     * @prop {String} default - The default value of the property
     * @description Controls the element type of the button component that
     * will be rendered
     */
    tag: {
      validator: (v) => types.indexOf(v) >= 0,
      type: String,
      default: 'button',
    },

    /**
     * @type {String|Object}
     * @description The to prop required for router-link components
     */
    to: {
      type: [String, Object],
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description Sets item to be disabled.
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Object}
     * @prop {Function}
     * @prop {String}
     * @description Controls the size of action (in width)
     */
    size: {
      validator: (v) => sizes.indexOf(v) >= 0,
      default: undefined,
      type: String,
    },

    /**
     * @type {Object}
     * @prop {Function}
     * @prop {String}
     * @description Whether the items inside should be displayed horizontally
     * rather than stacked
     */
    horizontal: {
      default: false,
      type: Boolean,
    },

    /**
     * @type {Object}
     * @prop {Function}
     * @prop {String}
     * @description Controls the type of the Icon i.e "fireball"
     */
    icon: {
      validator: (v) => iconTypes.indexOf(v) >= 0,
      default: undefined,
      type: String,
    },

    /**
     * @type {Object}
     * @prop {Function}
     * @prop {String}
     * @description Controls the size of the Icon i.e "large"
     */
    iconSize: {
      validator: (v) => iconSizes.indexOf(v) >= 0,
      default: 'large',
      type: String,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['click'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const computedTag = computed(() => {
      if (props.disabled !== undefined && props.disabled !== false) {
        return 'button'
      }
      return props.tag
    })

    const actionClasses = computed(() => {
      return {
        [`ak-action-grid__item--${props.size}`]: props.size,
        ['ak-action-grid__item--horizontal']: props.horizontal,
      }
    })

    const clicked = (e) => {
      /* istanbul ignore if */
      if (e && e.originalEvent instanceof Event) {
        e.preventDefault()
        e.stopPropagation()
      }
      emit('click', e)
    }

    return {
      computedTag,
      actionClasses,
      clicked,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-action-grid {
  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: $spacing-small 0;
    border: 2px solid transparent;
    outline: 0;
    border-radius: $base-border-radius;
    background-color: rgba($color-action, 0);
    color: inherit;
    text-align: center;
    flex-basis: 80px;
    flex-shrink: 1;
    flex-grow: 0;
    transition: $transition;

    &--none {
      flex-basis: 0;
    }

    &--small {
      flex-basis: 50px;
    }

    &--large {
      flex-basis: 110px;
    }

    &--huge {
      flex-basis: 150px;
    }

    &--full {
      flex-grow: 1;
      flex-basis: 100%;
    }

    &--horizontal {
      flex-direction: row;
      justify-content: center;
    }

    &:focus {
      border-color: $color-action;
    }
    &:hover {
      background-color: $color-action;
      color: #fff;
    }

    &:active {
      transform: scale(0.95);
    }
  }
}
</style>
